/**
 * Batches report (SPEC §8.7).
 *
 * The same panels as Finished Product, one level upstream: a batch is the juice
 * that goes into the bottle, judged against its **formula's** average spec
 * rather than any one SKU's limits. Reading order is the six spec trends --
 * Brix, Brix/acid ratio, titratable acidity, USDA color, recoverable oil, pulp
 * -- then the batch samples behind the selected week.
 *
 * The load-bearing rule, restated where the pixels are drawn: a sample missing
 * a spec is excluded from that spec's statistics, never counted as zero. The
 * backend enforces it (nulls skip polars aggregation); this component's part is
 * to plot a valueless week as a *gap* and a missing detail cell as a dash, so
 * the exclusion stays visible rather than smoothed over.
 *
 * Bands vary in coverage far more than on Finished Product, because the formula
 * average is only as complete as Spec Management is: Brix has a band on 99% of
 * batches, pulp on 15%, and no formula defines a pulp *target* at all. A panel
 * with no band is the honest rendering, not a bug.
 */

import type { CustomReportProps } from '..'
import { Card } from '../shared/Card'
import { count, dataset, text } from '../shared/rows'

import '../report.css'

import { SampleTable } from './SampleTable'
import type { WeekStat } from './SpecTrendChart'
import { SpecTrendChart } from './SpecTrendChart'
import { weekLabel } from './format'

/** The six panels, in the owner's reading order, at each spec's precision. */
const SPEC_LEGEND =
  'Dashed green is the target, dashed red the spec limits; a red triangle marks a week with out-of-spec samples.'

const PANELS = [
  {
    key: 'brix',
    title: 'Brix, weekly',
    subtitle: `Weekly average (line) over the min–max range (band). ${SPEC_LEGEND}`,
    decimals: 2,
  },
  {
    key: 'ratio',
    title: 'Brix/acid ratio, weekly',
    subtitle: `Samples with no ratio recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 2,
  },
  {
    key: 'acid',
    title: 'Titratable acidity (as citric), weekly',
    subtitle: `Samples with no acidity recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 3,
  },
  {
    key: 'color',
    title: 'USDA color, weekly',
    subtitle:
      'Weekly average (line) over the min–max range (band). No spec band is defined for color.',
    decimals: 1,
  },
  {
    key: 'oil',
    title: 'Recoverable oil, weekly',
    subtitle: `Samples with no oil recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 3,
  },
  {
    key: 'pulp',
    title: 'Pulp, weekly',
    subtitle: `Samples with no pulp recorded are excluded, never counted as zero. ${SPEC_LEGEND}`,
    decimals: 2,
  },
] as const

function asString(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function stat(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

export function FinishedProductBatches({ result, values, setParam }: CustomReportProps) {
  const weekly = dataset(result, 'weekly')
  const detail = dataset(result, 'primary')

  // The selected week comes from the server, not from a local fallback — the
  // server already resolved it to compute the detail rows.
  const selectedWeek = text(dataset(result, 'selection')[0]?.week ?? null)

  const site = asString(values.site)
  const formula = asString(values.formula)
  const totalSamples = weekly.reduce((sum, row) => sum + count(row.samples), 0)

  const perSpec = (key: string): WeekStat[] =>
    weekly.map((row) => ({
      week: text(row.week) ?? '',
      n: count(row[`${key}_n`]),
      avg: stat(row[`${key}_avg`]),
      min: stat(row[`${key}_min`]),
      max: stat(row[`${key}_max`]),
      // Absent for color (the view defines no color band) — `stat` reads the
      // missing columns as null and the chart draws no overlay.
      lo: stat(row[`${key}_lo`]),
      hi: stat(row[`${key}_hi`]),
      tgt: stat(row[`${key}_tgt`]),
      oos: count(row[`${key}_oos`]),
    }))

  const detailSubtitle =
    selectedWeek === null
      ? 'No week to show'
      : `Week of ${weekLabel(selectedWeek)} · ${detail.length.toString()} ${
          detail.length === 1 ? 'sample' : 'samples'
        }`

  return (
    <div className="bespoke-report">
      <p className="report-summary">
        {site === '' ? '' : `${site} · `}
        {formula === '' ? '' : `${formula} · `}
        {totalSamples.toLocaleString()}{' '}
        {totalSamples === 1 ? 'batch' : 'batches'} across {weekly.length.toString()}{' '}
        {weekly.length === 1 ? 'week' : 'weeks'}
      </p>

      {PANELS.map((panel) => (
        <Card
          key={panel.key}
          title={panel.title}
          subtitle={`${panel.subtitle} Select a week to load its samples below.`}
        >
          <SpecTrendChart
            data={perSpec(panel.key)}
            decimals={panel.decimals}
            selectedWeek={selectedWeek}
            onSelectWeek={(week) => {
              setParam('week', week)
            }}
          />
        </Card>
      ))}

      <Card title="Batch detail" subtitle={detailSubtitle}>
        <SampleTable rows={detail} />
      </Card>
    </div>
  )
}
